import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useDatabaseFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Load favorites from database when user logs in
  useEffect(() => {
    if (user) {
      loadFavoritesFromDatabase();
    } else {
      // Clear favorites when user logs out and load from localStorage for anonymous users
      setFavorites([]);
      localStorage.removeItem('favorites');
    }
  }, [user]);

  const loadFavoritesFromDatabase = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customer_favorites')
        .select('product_id')
        .eq('user_id', user.id);

      if (error) throw error;

      const productIds = data?.map(fav => fav.product_id) || [];
      setFavorites(productIds);
      
      // Sync to localStorage for offline access
      localStorage.setItem('favorites', JSON.stringify(productIds));
    } catch (error) {
      console.error('Error loading favorites:', error);
      // Fallback to localStorage
      loadFavoritesFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const loadFavoritesFromLocalStorage = () => {
    const stored = localStorage.getItem('favorites');
    if (stored) {
      try {
        setFavorites(JSON.parse(stored));
      } catch (error) {
        console.error('Error parsing favorites from localStorage:', error);
        setFavorites([]);
      }
    }
  };

  const syncFavoriteToDatabase = async (productId: string, isAdding: boolean) => {
    if (!user) return;

    try {
      if (isAdding) {
        const { error } = await supabase
          .from('customer_favorites')
          .insert({ user_id: user.id, product_id: productId });
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('customer_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId);
        
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error syncing favorite to database:', error);
      toast({
        title: "Sync Error",
        description: "Could not sync favorite to your account. Changes saved locally.",
        variant: "destructive",
      });
    }
  };

  const toggleFavorite = async (productId: string | number) => {
    const id = String(productId);
    const isCurrentlyFavorite = favorites.includes(id);
    const newFavorites = isCurrentlyFavorite
      ? favorites.filter(favId => favId !== id)
      : [...favorites, id];

    // Update state immediately for better UX
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));

    // Sync to database if user is logged in
    if (user) {
      await syncFavoriteToDatabase(id, !isCurrentlyFavorite);
    }
  };

  const addToFavorites = async (productId: string | number) => {
    const id = String(productId);
    if (!favorites.includes(id)) {
      const newFavorites = [...favorites, id];
      setFavorites(newFavorites);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));

      if (user) {
        await syncFavoriteToDatabase(id, true);
      }
    }
  };

  const removeFromFavorites = async (productId: string | number) => {
    const id = String(productId);
    const newFavorites = favorites.filter(favId => favId !== id);
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));

    if (user) {
      await syncFavoriteToDatabase(id, false);
    }
  };

  const isFavorite = (productId: string | number) => {
    const id = String(productId);
    return favorites.includes(id);
  };

  // Sync localStorage favorites to database when user logs in
  const syncLocalStorageToDatabase = async () => {
    if (!user) return;

    const localFavorites = localStorage.getItem('favorites');
    if (!localFavorites) return;

    try {
      const localFavoritesArray = JSON.parse(localFavorites);
      
      // Get current database favorites
      const { data: dbFavorites } = await supabase
        .from('customer_favorites')
        .select('product_id')
        .eq('user_id', user.id);

      const dbFavoriteIds = dbFavorites?.map(fav => fav.product_id) || [];
      
      // Find favorites that exist locally but not in database
      const favoritesToSync = localFavoritesArray.filter((id: string) => 
        !dbFavoriteIds.includes(id)
      );

      // Bulk insert missing favorites
      if (favoritesToSync.length > 0) {
        const { error } = await supabase
          .from('customer_favorites')
          .insert(
            favoritesToSync.map((product_id: string) => ({
              user_id: user.id,
              product_id
            }))
          );

        if (error) throw error;
      }

      // Reload favorites from database after sync
      await loadFavoritesFromDatabase();
    } catch (error) {
      console.error('Error syncing localStorage to database:', error);
    }
  };

  // Auto-sync when user logs in
  useEffect(() => {
    if (user) {
      syncLocalStorageToDatabase();
    }
  }, [user]);

  return {
    favorites,
    loading,
    toggleFavorite,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    syncLocalStorageToDatabase
  };
};